import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { matchesApi } from '../lib/api';
import { motion } from 'framer-motion';
import { MessageSquare, Calendar, Clock, Shield, ArrowLeft, RefreshCw, CheckCheck } from 'lucide-react';
import MatchChatDialog from './matches/MatchChatDialog';
import { toast } from 'sonner';

const isItalianFn = () => (typeof window !== 'undefined' && window.localStorage?.getItem('lang') !== 'en');

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); } catch { return ''; }
};
const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};
const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'ora';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}g`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

export default function MatchChats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMatchId, setOpenMatchId] = useState(null);
  const isItalian = isItalianFn();

  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await matchesApi.listMyMatchChats();
      setChats(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // Auto-open match chat from URL ?open=match_xxx
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) setOpenMatchId(openId);
  }, [searchParams]);

  // Refresh when dialog closes (in case user posted/read messages)
  useEffect(() => {
    if (openMatchId === null) fetchChats();
  }, [openMatchId, fetchChats]);

  const withMessages = chats.filter(c => c.last_message);
  const empty = chats.filter(c => !c.last_message);

  return (
    <div className="min-h-screen ept-bg-ambient text-white pb-12">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-heading text-2xl uppercase tracking-wider flex items-center gap-2">
                <MessageSquare size={20} className="text-[#00F0FF]" />
                {isItalian ? 'Chat Partite' : 'Match Chats'}
              </h1>
              <p className="text-xs text-slate-500 mt-1">
                {isItalian
                  ? 'Coordinati con i capitani avversari per le partite'
                  : 'Coordinate with opposing captains for your matches'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  await matchesApi.markAllMatchChatsRead();
                  toast.success(isItalian ? 'Tutte le chat segnate come lette' : 'All chats marked as read');
                  window.dispatchEvent(new CustomEvent('match_chat_read', { detail: { all: true } }));
                  fetchChats();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
                }
              }}
              className="flex items-center gap-1.5 text-xs text-[#00F0FF] hover:text-white px-2.5 py-1.5 bg-[#00F0FF]/5 border border-[#00F0FF]/20 hover:bg-[#00F0FF]/15 rounded transition-colors"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck size={14} />
              <span className="hidden sm:inline">{isItalian ? 'Segna tutto come letto' : 'Mark all as read'}</span>
              <span className="sm:hidden">{isItalian ? 'Letto' : 'Read'}</span>
            </button>
            <button
              onClick={fetchChats}
              className="text-slate-400 hover:text-white p-2"
              disabled={loading}
              data-testid="refresh-chats-btn"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-16 bg-[#0a0d15] border border-white/10 rounded">
            <MessageSquare size={32} className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">
              {isItalian
                ? 'Nessuna chat disponibile. Le chat appaiono per le partite del tuo club.'
                : 'No chats available. Chats appear for your club\'s matches.'}
            </p>
          </div>
        ) : (
          <>
            {/* With messages first */}
            {withMessages.length > 0 && (
              <div className="space-y-2 mb-6" data-testid="chats-with-messages">
                <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                  {isItalian ? 'Conversazioni attive' : 'Active conversations'}
                </h2>
                {withMessages.map(c => <ChatRow key={c.match_id} chat={c} onOpen={setOpenMatchId} isItalian={isItalian} />)}
              </div>
            )}

            {/* Without messages */}
            {empty.length > 0 && (
              <div className="space-y-2" data-testid="chats-empty">
                <h2 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                  {isItalian ? 'Inizia una conversazione' : 'Start a conversation'}
                </h2>
                {empty.map(c => <ChatRow key={c.match_id} chat={c} onOpen={setOpenMatchId} isItalian={isItalian} />)}
              </div>
            )}
          </>
        )}
      </motion.div>

      {openMatchId && (
        <MatchChatDialog
          open={!!openMatchId}
          onOpenChange={(open) => { if (!open) setOpenMatchId(null); }}
          matchId={openMatchId}
          isItalian={isItalian}
          currentUserId={user?.user_id}
        />
      )}
    </div>
  );
}

const ChatRow = ({ chat, onOpen, isItalian }) => {
  const last = chat.last_message;
  const unread = chat.unread_count || 0;
  const matchInfo = chat.match_date
    ? `${formatDate(chat.match_date)} · ${formatTime(chat.match_date)}`
    : (isItalian ? 'Da programmare' : 'TBD');
  return (
    <button
      type="button"
      onClick={() => onOpen(chat.match_id)}
      className={`w-full text-left bg-[#0a0d15] border rounded p-3 hover:bg-white/5 transition-colors ${
        unread > 0 ? 'border-[#00F0FF]/40' : 'border-white/10'
      }`}
      data-testid={`chat-row-${chat.match_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 w-10 h-10 rounded bg-[#050508] border border-white/10 flex items-center justify-center">
          <MessageSquare size={16} className={unread > 0 ? 'text-[#00F0FF]' : 'text-slate-500'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-sm truncate">{chat.home_club?.name}</span>
            <span className="text-slate-500 text-xs">vs</span>
            <span className="font-bold text-sm truncate">{chat.away_club?.name}</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            {chat.match_day != null && (
              <span className="flex items-center gap-1">
                <Shield size={11} className="text-[#FFEA00]" /> G{chat.match_day}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {matchInfo}
            </span>
            {chat.status === 'completed' && (
              <span className="text-[#00E676]">{isItalian ? 'completata' : 'completed'}</span>
            )}
          </div>
          {last ? (
            <div className="text-xs text-slate-300 mt-1 truncate">
              <span className="text-slate-500">{last.sender_gamertag || last.sender_name}:</span>{' '}
              {last.content}
            </div>
          ) : (
            <div className="text-xs text-slate-600 italic mt-1">
              {isItalian ? 'Nessun messaggio ancora' : 'No messages yet'}
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {last && (
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock size={10} /> {formatRelative(last.created_at)}
            </span>
          )}
          {unread > 0 && (
            <span className="bg-[#00F0FF] text-black text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};
